import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useSuiteStore } from './useSuiteStore'
import { ORDER_STATUS } from '@/constants/orderStatus'
import { useOrderDerogationStore } from './useOrderDerogationStore'

interface PaginatedOrders {
  data: any[]
  total: number
  page: number
  pageSize: number
  totalPages: number
  statusCounts: Record<string, { count: number; sumMarge: number }>
}

export const useOrderStore = defineStore('order', () => {
  const suiteStore = useSuiteStore()
  const orderDerogationStore = useOrderDerogationStore()
  const orderModels = ref<any[]>([])
  const ventilationFamilies = ref<{ code: string; label: string }[]>([])
  const isLoadingModels = ref(false)
  let catalogLoadGeneration = 0
  let ensureOrderModelsPromise: Promise<any[]> | null = null
  let ensureVentilationFamiliesPromise: Promise<{ code: string; label: string }[]> | null = null
  const orders = ref<PaginatedOrders | null>({ data: [], page: 1, pageSize: 10, total: 0, totalPages: 0, statusCounts: {} })
  const orderUuids = ref<string[]>([])
  const isLoadingOrders = ref(false)
  const statusCounts = ref<Record<string, { count: number; sumMarge: number }>>({})

  const statusOptions = ref([
    { id: 1, order: 1, value: ORDER_STATUS.DRAFT, label: 'Brouillon' },
    { id: 8, order: 2, value: ORDER_STATUS.SENT_TO_BU_MANAGER, label: 'Envoyé Manager' },
    { id: 2, order: 3, value: ORDER_STATUS.WAITING, label: 'En attente' },
    { id: 3, order: 6, value: ORDER_STATUS.VALIDATED, label: 'Validée' },
    { id: 4, order: 4, value: ORDER_STATUS.BLOCKED_BUSINESS, label: 'Bloquée Business' },
    { id: 5, order: 5, value: ORDER_STATUS.BLOCKED_OPERATOR, label: 'Bloquée Opérateur' },
    { id: 6, order: 7, value: ORDER_STATUS.CANCELED, label: 'Annulée' },
    { id: 7, order: 8, value: ORDER_STATUS.IN_PROGRESS, label: 'En cours' },
  ])

  const rankings = ref<any>()

  const getStatusLabel = (status: string) => {
    const s = String(status || '').toUpperCase()
    const option = statusOptions.value.find((opt) => opt.value === s)
    if (option) return option.label

    // Fallback pour les cas non gérés explicitement si nécessaire
    if (s.startsWith('BLOCKED_')) {
      return s.replace('BLOCKED_', 'Bloquée ').replace('_', ' ')
    }

    return status || ''
  }

  const fetchAdvUsers = async () => {
    try {
      const response = await suiteStore.suiteFetch('users/role/adv', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      })
      if (response && typeof response !== 'boolean' && response.ok) {
        return await response.json()
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des ADV:', error)
    }
    return []
  }

  const fetchOrdersForExport = async (filters: any = {}): Promise<any[]> => {
    const MAX_EXPORT = 50000
    const result = await fetchOrders(filters, MAX_EXPORT, 1)
    if (Array.isArray(result)) return orders.value?.data ?? []
    return result.data ?? orders.value?.data ?? []
  }

  const fetchOrders = async (filters: any = {}, pageSize: number, page: number) => {
    isLoadingOrders.value = true
    const body = {
      filters,
      pageSize,
      page,
    }
    try {
      const response = await suiteStore.suiteFetch('orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        credentials: 'include',
      })
      if (response && typeof response !== 'boolean' && response.ok) {
        const data = await response.json()
        orders.value = {
          data: Array.isArray(data.data) ? data.data : (data.data ?? []),
          total: data?.total ?? 0,
          page: data?.page ?? page,
          pageSize: data?.pageSize ?? pageSize,
          totalPages: data?.totalPages ?? 1,
          statusCounts: data?.statusCounts ?? {},
        }
        statusCounts.value = data?.statusCounts ?? {}
        return orders.value
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des commandes:', error)
    } finally {
      isLoadingOrders.value = false
    }
    return []
  }

  const fetchOrderModels = async () => {
    const gen = catalogLoadGeneration
    isLoadingModels.value = true
    try {
      const response = await suiteStore.suiteFetch('order-models/code/UNIXO', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
        },
        credentials: 'include',
      })
      if (response && typeof response !== 'boolean' && response.ok) {
        const data = await response.json()
        if (gen === catalogLoadGeneration) {
          orderModels.value = data
        }
        return data
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des modèles de commande:', error)
    } finally {
      isLoadingModels.value = false
    }
    return []
  }

  const fetchFamiliesForVentilation = async () => {
    const gen = catalogLoadGeneration
    try {
      const response = await suiteStore.suiteFetch('order-models/families/ventilation', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      })
      if (response && typeof response !== 'boolean' && response.ok) {
        const data = await response.json()
        const mapped = Array.isArray(data)
          ? data.map((f: { code: string; label?: string | null }) => ({
              code: f.code,
              label: f.label || f.code,
            }))
          : []
        if (gen === catalogLoadGeneration) {
          ventilationFamilies.value = mapped
        }
        return mapped
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des familles:', error)
    }
    return []
  }

  // Helper générique pour gérer le pattern "ensure" avec cache et promise unique
  const createEnsureFunction = <T>(
    cache: { value: T },
    promiseHolder: { current: Promise<T> | null },
    fetchFn: () => Promise<T>,
  ) => {
    return async (): Promise<T> => {
      if ((cache.value as any)?.length > 0) return cache.value
      if (promiseHolder.current) return promiseHolder.current
      promiseHolder.current = (async () => {
        await fetchFn()
        return cache.value
      })()
      try {
        return await promiseHolder.current
      } finally {
        promiseHolder.current = null
      }
    }
  }

  const orderModelsPromiseHolder = { current: ensureOrderModelsPromise }
  const ensureOrderModels = createEnsureFunction(
    orderModels,
    orderModelsPromiseHolder,
    fetchOrderModels,
  )

  const ventilationFamiliesPromiseHolder = { current: ensureVentilationFamiliesPromise }
  const ensureVentilationFamilies = createEnsureFunction(
    ventilationFamilies,
    ventilationFamiliesPromiseHolder,
    fetchFamiliesForVentilation,
  )

  /** Modèle de formulaire par code (référentiel) ; charge le cache si besoin. */
  const getOrderTemplateByCode = async (code: string | null | undefined): Promise<any | null> => {
    if (!code) return null
    await ensureOrderModels()
    return orderModels.value.find((m: any) => m.code === code) ?? null
  }

  const clearOrderCatalog = () => {
    catalogLoadGeneration++
    orderModels.value = []
    ventilationFamilies.value = []
    orderModelsPromiseHolder.current = null
    ventilationFamiliesPromiseHolder.current = null
  }

  // Helper pour extraire un message d'erreur depuis une réponse HTTP
  const extractErrorMessage = async (response: Response | boolean): Promise<string | undefined> => {
    if (!response || typeof response === 'boolean') return undefined
    try {
      const err = await response.json()
      return err?.message ?? err?.error
    } catch {
      return undefined
    }
  }

  // Helper pour préparer le body et headers d'une requête avec FormData ou JSON
  const prepareRequestOptions = (data: any, method: string) => {
    const isFormData = data instanceof FormData
    const headers: Record<string, string> = {}
    if (!isFormData) headers['Content-Type'] = 'application/json'
    return {
      method,
      headers,
      body: isFormData ? data : JSON.stringify(data),
      credentials: 'include' as RequestCredentials,
    }
  }

  const createOrder = async (orderData: any): Promise<{ ok: boolean; orderUuid?: string; errorMessage?: string }> => {
    try {
      const response = await suiteStore.suiteFetch('orders/order', prepareRequestOptions(orderData, 'POST'))
      if (response && typeof response !== 'boolean' && response.ok) {
        const data = await response.json()
        const order = data?.order ?? data?.data ?? data
        const orderUuid = order?.uuid ?? order?.orderUuid

        orderDerogationStore.fetchCheckDerogation(orderUuid) // Pré-chargement des infos de dérogation pour éviter les latences lors de l'affichage
        return { ok: true, orderUuid }
      }
      const errorMessage = await extractErrorMessage(response)
      return { ok: false, errorMessage }
    } catch (error) {
      console.error('Erreur lors de la création de la commande:', error)
      return { ok: false }
    }
  }

  const currentOrder = ref<any | null>(null)

  const fetchOrderByQuote = async (quoteUuid: string) => {
    try {
      const response = await suiteStore.suiteFetch(`orders/by-quote/${quoteUuid}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      })
      if (response && typeof response !== 'boolean' && response.ok) {
        const data = await response.json()
        return data ?? null
      }
    } catch (error) {
      console.error('Erreur lors de la récupération de la commande par devis:', error)
    }
    return null
  }

  const fetchOrder = async (orderUuid: string, options?: { forceSync?: boolean }) => {
    try {
      const qs = options?.forceSync ? '?forceSync=true' : ''
      const response = await suiteStore.suiteFetch(`orders/order/${orderUuid}${qs}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      })
      if (response && typeof response !== 'boolean' && response.ok) {
        const data = await response.json()
        currentOrder.value = data?.order ?? data?.data ?? data
        return currentOrder.value
      }
    } catch (error) {
      console.error('Erreur lors de la récupération de la commande:', error)
    }
    return null
  }

  const updateOrder = async (orderUuid: string, orderData: any): Promise<{ ok: boolean; errorMessage?: string }> => {
    try {
      const response = await suiteStore.suiteFetch(`orders/order/${orderUuid}`, prepareRequestOptions(orderData, 'PATCH'))
      if (response && typeof response !== 'boolean' && response.ok) {
        return { ok: true }
      }
      const errorMessage = await extractErrorMessage(response)
      return { ok: false, errorMessage }
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la commande:', error)
      return { ok: false }
    }
  }

  const fetchSimulateCommission = async (orderUuid: string) => {
    try {
      const response = await suiteStore.suiteFetch(`orders/${orderUuid}/simulate-commission`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      })
      if (response && typeof response !== 'boolean' && response.ok) {
        const data = await response.json()
        if (currentOrder.value) {
          currentOrder.value = { ...currentOrder.value, simulateCommission: data }
        }
        return data
      }
    } catch (error) {
      console.error('Erreur lors de la simulation de commission:', error)
    }
    return null
  }

  const quoteSimulateCommission = ref<any | null>(null)

  const fetchSimulateCommissionFromQuote = async (quoteRef: string) => {
    quoteSimulateCommission.value = null
    try {
      const response = await suiteStore.suiteFetch(`orders/simulate-commission/quote/${quoteRef}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      })
      if (response && typeof response !== 'boolean' && response.ok) {
        const data = await response.json()
        quoteSimulateCommission.value = data
        return data
      }
    } catch (error) {
      console.error('Erreur lors de la simulation de commission depuis le devis:', error)
    }
    return null
  }

  const fetchLeadsByCompany = async (companyUuid: string) => {
    try {
      const response = await suiteStore.suiteFetch(`companies/${companyUuid}/leads`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      })
      if (response && typeof response !== 'boolean' && response.ok) {
        const data = await response.json()
        return Array.isArray(data) ? data : (data?.data ?? [])
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des leads:', error)
    }
    return []
  }

  const fetchAutoLinkedLead = async (quoteRef: string, familyCode: string) => {
    try {
      const params = new URLSearchParams({ quoteRef, familyCode })
      const response = await suiteStore.suiteFetch(`orders/auto-linked-lead?${params.toString()}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      })
      if (response && typeof response !== 'boolean' && response.ok) {
        return await response.json()
      }
    } catch (error) {
      console.error('Erreur lors de la résolution du lead auto-rattaché:', error)
    }
    return null
  }

  // Commandes VALIDÉES d'une société (endpoint dédié `orders/portal-by-company`).
  // Renvoie : { uuid, status, createdAt, validatedAt, quote: { quoteReference,
  // customerReference, totalCa, totalCaUntaxed }, delivery: { status, ... } }
  const companyOrders = ref<any[]>([])
  const isLoadingCompanyOrders = ref(false)
  const fetchValidatedOrdersByCompany = async (companyUuid: string) => {
    isLoadingCompanyOrders.value = true
    try {
      const response = await suiteStore.suiteFetch(
        `orders/portal-by-company?companyUuid=${encodeURIComponent(companyUuid)}`,
        { method: 'GET', credentials: 'include' },
      )
      if (response && typeof response !== 'boolean' && response.ok) {
        const data = await response.json()
        companyOrders.value = Array.isArray(data) ? data : (data?.data ?? [])
        return companyOrders.value
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des commandes de la société:', error)
    } finally {
      isLoadingCompanyOrders.value = false
    }
    companyOrders.value = []
    return []
  }

  const clearCurrentOrder = () => {
    currentOrder.value = null
  }

  const deleteOrderFile = async (orderFileUuid: string) => {
    try {
      const response = await suiteStore.suiteFetch(`orders/file/${orderFileUuid}`, {
        method: 'DELETE',
        credentials: 'include',
      })
      if (response && typeof response !== 'boolean' && response.ok && currentOrder.value?.orderFiles) {
        currentOrder.value.orderFiles = currentOrder.value.orderFiles.filter((f: any) => f.uuid !== orderFileUuid)
      }
      return response && typeof response !== 'boolean' && response.ok
    } catch (error) {
      console.error('Erreur lors de la suppression du fichier:', error)
      return false
    }
  }

  const startProcessing = async (orderUuid: string) => {
    try {
      const response = await suiteStore.suiteFetch(`orders/${orderUuid}/start-processing`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      })
      if (response && typeof response !== 'boolean' && response.ok) {
        if (currentOrder.value && currentOrder.value.status === ORDER_STATUS.WAITING) {
          currentOrder.value.status = ORDER_STATUS.IN_PROGRESS
        }
        return true
      }
    } catch (error) {
      console.error('Erreur lors du démarrage du traitement:', error)
    }
    return false
  }

  const changeOrderStatus = async (orderUuid: string, status: string, comment: string) => {
    try {
      const response = await suiteStore.suiteFetch(`orders/${orderUuid}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, comment }),
        credentials: 'include',
      })
      if (response && typeof response !== 'boolean' && response.ok) {
        if (currentOrder.value) {
          currentOrder.value.status = status
        }
        return true
      }
    } catch (error) {
      console.error('Erreur lors du changement de statut:', error)
    }
    return false
  }

  const switchAdv = async (orderUuid: string, advUuid: string) => {
    try {
      const response = await suiteStore.suiteFetch(`orders/order/${orderUuid}/switch-adv/${advUuid}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      })
      if (response && typeof response !== 'boolean' && response.ok) {
        if (currentOrder.value) {
          // Mise à jour automatique du statut si en attente
          if (currentOrder.value.status === ORDER_STATUS.WAITING) {
            currentOrder.value.status = ORDER_STATUS.IN_PROGRESS
          }

          // Mise à jour de l'ADV assigné (on couvre les différents champs possibles)
          if (currentOrder.value.advUuid !== undefined) currentOrder.value.advUuid = advUuid
          if (currentOrder.value.adv_uuid !== undefined) currentOrder.value.adv_uuid = advUuid
          if (currentOrder.value.assignedToUuid !== undefined) currentOrder.value.assignedToUuid = advUuid
          if (currentOrder.value.assigned_to_uuid !== undefined) currentOrder.value.assigned_to_uuid = advUuid

          // On rafraîchit l'objet complet de l'ADV si on a la liste des ADV
          const advUser = await fetchAdvUsers().then((users) => users.find((u: any) => u.uuid === advUuid))
          if (advUser) {
            if (currentOrder.value.adv !== undefined) currentOrder.value.adv = advUser
            if (currentOrder.value.assignedTo !== undefined) currentOrder.value.assignedTo = advUser
          }
        }
        return true
      }
    } catch (error) {
      console.error("Erreur lors du changement d'ADV:", error)
    }
    return false
  }

  const addOperatorComment = async (orderUuid: string, comment: string) => {
    const response = await suiteStore.suiteFetch(`orders/${orderUuid}/operator-comment`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ comment }),
      credentials: 'include',
    })
    if (!response || typeof response === 'boolean' || !response.ok) throw new Error('Erreur lors de la création du commentaire')
    return response.json()
  }

  const updateOperatorComment = async (orderUuid: string, commentUuid: string, comment: string) => {
    const response = await suiteStore.suiteFetch(`orders/${orderUuid}/operator-comment/${commentUuid}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ comment }),
      credentials: 'include',
    })
    if (!response || typeof response === 'boolean' || !response.ok) throw new Error('Erreur lors de la modification du commentaire')
    return response.json()
  }

  const deleteOperatorComment = async (orderUuid: string, commentUuid: string) => {
    const response = await suiteStore.suiteFetch(`orders/${orderUuid}/operator-comment/${commentUuid}`, {
      method: 'DELETE',
      credentials: 'include',
    })
    if (!response || typeof response === 'boolean' || !response.ok) throw new Error('Erreur lors de la suppression du commentaire')
    return response.json()
  }

  const favoriteOperatorComment = async (orderUuid: string, commentUuid: string) => {
    const response = await suiteStore.suiteFetch(`orders/${orderUuid}/operator-comment/${commentUuid}/favorite`, {
      method: 'PATCH',
      credentials: 'include',
    })
    if (!response || typeof response === 'boolean' || !response.ok) throw new Error('Erreur lors de la mise en favori')
    return response.json()
  }

  const deleteOrder = async (orderUuid: string) => {
    try {
      const response = await suiteStore.suiteFetch(`orders/order/${orderUuid}`, {
        method: 'DELETE',
        credentials: 'include',
      })
      return response && typeof response !== 'boolean' && response.ok
    } catch (error) {
      console.error('Erreur lors de la suppression de la commande:', error)
      return false
    }
  }

  // Helper pour télécharger un fichier depuis une réponse HTTP
  const downloadFileFromResponse = async (response: Response, defaultFileName: string): Promise<boolean> => {
    if (!response.ok) return false

    const contentDisposition = response.headers.get('Content-Disposition')
    let fileName = defaultFileName

    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?(.+)"?/)
      if (match) fileName = match[1]
    }

    const blob = await response.blob()
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = fileName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)

    return true
  }

  const downloadZip = async (fileUuids: string[]) => {
    try {
      const response = await suiteStore.suiteFetch(`orders/download/zip`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileUuids }),
        credentials: 'include',
      })

      if (response && response.ok) {
        return await downloadFileFromResponse(response, 'fichiers.zip')
      }

      return false
    } catch (error) {
      console.error('Erreur lors du telechargement:', error)
      return false
    }
  }

  const getRanking = async (params?: {
    period?: 'month' | 'year'
    year?: number
    yearMonth?: string
    yearMonths?: string[]
  }) => {
    isLoadingOrders.value = true
    const period = params?.period ?? 'month'
    let date: string
    let months: string[] | undefined
    if (period === 'year') {
      const y = params?.year ?? new Date().getFullYear()
      date = `${y}-01-01`
    } else {
      const validMonths = (params?.yearMonths ?? []).filter((m) => /^\d{4}-\d{2}$/.test(m))
      if (validMonths.length > 0) {
        months = validMonths
        date = `${validMonths[0]}-01`
      } else {
        const ym = params?.yearMonth
        if (ym && /^\d{4}-\d{2}$/.test(ym)) {
          date = `${ym}-01`
        } else {
          date = new Date().toISOString().split('T')[0]
        }
      }
    }
    const body: { date: string; period: 'month' | 'year'; months?: string[] } = { date, period }
    if (months) body.months = months
    try {
      const response = await suiteStore.suiteFetch('orders/ranking', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        credentials: 'include',
      })
      if (response && typeof response !== 'boolean' && response.ok) {
        rankings.value = await response.json()
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des commandes:', error)
    } finally {
      isLoadingOrders.value = false
    }
  }
  const downloadTemplateZip = async () => {
    try {
      const response = await suiteStore.suiteFetch(`orders/download/template-zip`, {
        method: 'GET',
        credentials: 'include',
      })
      if (response && response.ok) {
        return await downloadFileFromResponse(response, 'fichiers-modele-commande.zip')
      }
      return false
    } catch (error) {
      console.error('Erreur lors du téléchargement des fichiers modèles:', error)
      return false
    }
  }

  const validateBuManager = async (orderUuid: string): Promise<{ ok: boolean; errorMessage?: string }> => {
    try {
      const response = await suiteStore.suiteFetch(`orders/${orderUuid}/validate-manager`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      })
      if (response && typeof response !== 'boolean' && response.ok) {
        if (currentOrder.value) {
          currentOrder.value.status = ORDER_STATUS.WAITING
        }
        return { ok: true }
      }
      const errorMessage = await extractErrorMessage(response)
      return { ok: false, errorMessage }
    } catch (error) {
      console.error('Erreur lors de la validation manager BU:', error)
      return { ok: false }
    }
  }

  const refuseBuManager = async (orderUuid: string, comment: string): Promise<{ ok: boolean; errorMessage?: string }> => {
    try {
      const response = await suiteStore.suiteFetch(`orders/${orderUuid}/refuse-manager`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ comment }),
      })
      if (response && typeof response !== 'boolean' && response.ok) {
        if (currentOrder.value) {
          currentOrder.value.status = ORDER_STATUS.DRAFT
        }
        return { ok: true }
      }
      const errorMessage = await extractErrorMessage(response)
      return { ok: false, errorMessage }
    } catch (error) {
      console.error('Erreur lors du refus manager BU:', error)
      return { ok: false }
    }
  }

  const changeOrderDateValidatedAt = async (orderUuid: string, orderData: any) => {
    try {
      const response = await suiteStore.suiteFetch(`orders/${orderUuid}/validatedAt`, prepareRequestOptions(orderData, 'PATCH'))
      if (response && typeof response !== 'boolean' && response.ok) {
        return { ok: true }
      }
      const errorMessage = await extractErrorMessage(response)
      return { ok: false, errorMessage }
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la commande:', error)
      return { ok: false }
    }
  }

  const exportOrdersCsv = async (
    dateFrom: string,
    dateTo: string,
    filters: {
      authorUuid?: string
      search?: string
      status?: string
      orderTemplateCode?: string[]
      sentAtFrom?: string
      sentAtTo?: string
      hideChildren?: boolean
    } = {},
  ) => {
    try {
      const response = await suiteStore.suiteFetch('orders/export-csv', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ dateFrom, dateTo, ...filters }),
      })
      if (response && response.ok) {
        return await downloadFileFromResponse(response, `export_${dateFrom}_${dateTo}.csv`)
      }
      return false
    } catch (error) {
      console.error('Erreur lors de l\'export CSV:', error)
      return false
    }
  }



  return {
    statusOptions,
    orderModels,
    ventilationFamilies,
    isLoadingModels,
    orders,
    orderUuids,
    isLoadingOrders,
    fetchOrders,
    currentOrder,
    clearCurrentOrder,
    fetchOrderModels,
    fetchFamiliesForVentilation,
    ensureOrderModels,
    ensureVentilationFamilies,
    getOrderTemplateByCode,
    clearOrderCatalog,
    createOrder,
    fetchOrder,
    fetchOrderByQuote,
    updateOrder,
    fetchSimulateCommission,
    quoteSimulateCommission,
    fetchSimulateCommissionFromQuote,
    fetchLeadsByCompany,
    fetchAutoLinkedLead,
    companyOrders,
    isLoadingCompanyOrders,
    fetchValidatedOrdersByCompany,
    deleteOrderFile,
    startProcessing,
    changeOrderStatus,
    downloadZip,
    downloadTemplateZip,
    statusCounts,
    getStatusLabel,
    getRanking,
    rankings,
    fetchAdvUsers,
    switchAdv,
    deleteOrder,
    fetchOrdersForExport,
    changeOrderDateValidatedAt,
    exportOrdersCsv,
    validateBuManager,
    addOperatorComment,
    updateOperatorComment,
    deleteOperatorComment,
    favoriteOperatorComment,
    refuseBuManager,
  }
})
